/**
 * ProductHeroMetrics — kompakte Kennzahlen (Preis netto / Marke / 30 Tage).
 * Max 100 lines.
 */

import { useTranslation } from 'react-i18next'
import type { Product } from '@/types/products'
import { useBrand } from '@/hooks/useBrands'
import { useSalesMetrics } from '@/hooks/useSalesMetrics'

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="product-hero-metric">
      <div className="text-2xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400 mb-0.5">
        {label}
      </div>
      <div className="text-base font-bold text-neutral-900 dark:text-neutral-50 leading-tight">
        {children}
      </div>
    </div>
  )
}

export function ProductHeroMetrics({ product }: { product: Product }) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language || 'de'
  const brandName = useBrand(product.brand_id ?? '').data?.name ?? null
  const sales = useSalesMetrics(product.id).data?.metrics

  const revenue30d = sales ? Number(sales.revenue_30d) : null
  const qty30d = sales?.quantity_30d ?? 0

  return (
    <div className="product-hero-metrics flex items-start gap-8 flex-wrap">
      <Stat label={t('products.priceNet')}>
        {product.price_net != null
          ? product.price_net.toLocaleString(locale, { style: 'currency', currency: 'EUR' })
          : '–'}
      </Stat>

      <Stat label={t('products.hero.brand')}>
        {brandName ?? <span className="text-neutral-400 font-normal">{t('products.hero.noBrand')}</span>}
      </Stat>

      <Stat label={t('products.sales30d')}>
        {revenue30d != null
          ? revenue30d.toLocaleString(locale, { style: 'currency', currency: 'EUR' })
          : '–'}
        {revenue30d != null && qty30d > 0 && (
          <span className="block text-2xs font-normal text-neutral-400">
            {t('products.sales30dPieces', { count: qty30d })}
          </span>
        )}
      </Stat>
    </div>
  )
}
